import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { Fragment, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { MainContent } from './MainContent';

import Button from '@/components/Button';
import { BellIcon } from '@/components/Navbar/Icons';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import ROUTES from '@/router/routes';
import { cn } from '@/utils';

interface Props {
  isPremium?: boolean;
}

export const NotificationsBell = ({ isPremium }: Props) => {
  const { isMobile } = useIsDesktop();
  const navigate = useNavigate();
  const notify = useToast();

  const { notifications, markAsRead, unreadCount, readCount, setShowRead } =
    useNotifications();

  const hasNotifications = unreadCount > 0 || readCount > 0;

  const handleClickBell = () => {
    if (unreadCount > 0) {
      markAsRead(notifications);
    }
  };

  const classNamesButton = useMemo(() => {
    return cn(
      'rounded-[.63rem] lg:rounded-[.88rem] bg-gdt-gray bg-opacity-30 w-[32px] h-[32px] lg:w-[39px] lg:h-[39px] flex justify-center items-center text-white lg:mr-10 hover:bg-gdt-blue relative p-0 border-none outline-none',
      {
        'bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-40': isPremium,
      }
    );
  }, [isPremium]);

  const BellNotificationUI = useCallback(() => {
    return (
      <>
        <BellIcon className="scale-[.66]" />
        {hasNotifications && (
          <div
            className={cn(
              'absolute top-[4px] right-[4px] bg-white text-black text-3xs font-bold w-4 h-4 flex items-center justify-center rounded-full border-none',
              { 'w-2 h-2 top-2 right-2': unreadCount === 0 }
            )}
          >
            {unreadCount > 0 ? unreadCount : ''}
          </div>
        )}
      </>
    );
  }, [hasNotifications, unreadCount]);

  if (!hasNotifications) {
    return (
      <Button
        className={classNamesButton}
        type="button"
        variant="primary"
        onClick={() => notify.show('No tenés notificaciones')}
      >
        <BellIcon className="scale-[.66]" />
      </Button>
    );
  }

  if (isMobile) {
    return (
      <Button
        className={classNamesButton}
        type="button"
        variant="primary"
        onClick={() => {
          setShowRead(false);
          handleClickBell();
          navigate(ROUTES.notifications);
        }}
      >
        <BellNotificationUI />
      </Button>
    );
  }

  return (
    <Popover className="relative z-50">
      <PopoverButton className={classNamesButton} onClick={handleClickBell}>
        <BellNotificationUI />
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel className="absolute right-0 top-full mt-2 w-screen max-w-sm px-4 sm:px-0">
          <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 bg-white flex flex-col">
            <div className="py-4 bg-white max-h-[400px] overflow-auto flex flex-col gap-4">
              <MainContent />
            </div>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};

export default NotificationsBell;
